<template>
    <AdminLayout>
        <template #topbar-search>
            <div class="flex flex-column ">
                <IconField>
                    <InputIcon class="pi pi-search" />
                    <InputText v-model="searchValue" placeholder="Search by keywords..." size="small" />
                </IconField>
            </div>
        </template>
        <template #header>
            <Breadcrumb :items="items" />
            <Button v-if="isSuperAdmin" label="Add User" class="accent-button" @click="userAdd" />
        </template>
        <div v-if="currentView !== 'list'" class="back-section">
            <Button icon="pi pi-arrow-left" label="Back" class="p-button-text Caption back-button mb-2"
                @click="goBack" />
        </div>
        <div class="container">
            <UserList v-if="currentView === 'list'" :tableData="tableData" :isLoading="isLoading"
                :first="(currentPage - 1) * rowsPerPage" :rowsPerPage="rowsPerPage" :totalRecords="totalRecords"
                :currentPage="currentPage" :isSuperAdmin="isSuperAdmin" @page-change="onPageChange"
                @status-change="onStatusChange" @edit-Admin="editAdmin" @delete-Admin="deleteAdminFn" />
            <AddUser v-else-if="currentView === 'add'" @add-user="addUserFn" mode="add" @cancel="cancelEdit"/>
            <AddUser v-else-if="currentView === 'edit'" :adminDetails="adminDetails" @update-user="updateUserFn"
                @cancel="cancelEdit" mode="edit" />
        </div>
        <ConfirmDialog></ConfirmDialog>
    </AdminLayout>
</template>

<script setup>
import { ref, computed, onMounted, defineEmits } from "vue";
import { useAuthStore } from "@/stores/authStore";
import AdminLayout from "@/layout/AdminLayout.vue";
import UserList from "@/components/dashboard/admin/users/userList.vue";
import AddUser from "@/components/dashboard/admin/users/addUser.vue";
import Breadcrumb from "@/components/CustomBreadcrumb.vue"
import Button from 'primevue/button';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import ConfirmDialog from 'primevue/confirmdialog';
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { getUsers, getAdmin, updateAdminUsername, updateAdminEmail, updateAdminRole, updateAdminStatus, updateAdminPassword, deleteAdmin } from "@/api/admin";

const toast = useToast();
const confirm = useConfirm();
const authStore = useAuthStore();
const currentView = ref("list");
const tableData = ref([]);
const isLoading = ref(false);
const totalRecords = ref(0);
const rowsPerPage = ref(10);
const currentPage = ref(1);
const searchValue = ref("");
const adminDetails = ref(null);
const keycloakRole = ref(null);
const isSuperAdmin = computed(() => authStore.user?.role === "super_admin");

const emit = defineEmits(["registration-success",]);

const fetchUsers = async () => {
    try {
        isLoading.value = true;
        const response = await getUsers({
            page: currentPage.value,
            limit: rowsPerPage.value,
            searchTerm: searchValue.value
        });
        tableData.value = response.data.data.admins.map(admin => ({
            ...admin,
            enabled: admin.enabled ?? true
        }));
        totalRecords.value = response.data.data.pagination.total;
    } catch (error) {
        console.error("Failed to fetch students:", error);
    } finally {
        isLoading.value = false;
    }
};

onMounted(fetchUsers);

const items = computed(() => {
    const breadcrumbItems = [{ label: "Users" }];
    if (currentView.value === "add") {
        breadcrumbItems.push({ label: "Add User" });
    }
    if (currentView.value === "edit") {
        breadcrumbItems.push({ label: "Edit User" });
    }
    return breadcrumbItems;
});

const onStatusChange = async (rowData) => {
    if (!isSuperAdmin.value) return;
    try {
        await updateAdminStatus(rowData._id);
        fetchUsers();
    } catch (err) {
        console.error('Failed to update status', err);
    }
};
const deleteAdminFn = (rowData) => {
    if (!isSuperAdmin.value) return;
    confirm.require({
        message: `Do you want to delete ${rowData.username || 'this user'}?`,
        header: 'Danger Zone',
        icon: 'pi pi-info-circle',
        rejectProps: {
            label: 'Cancel',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Delete',
            severity: 'danger'
        },
        accept: async () => {
            try {
                await deleteAdmin(rowData._id);
                toast.add({
                    severity: 'success',
                    summary: 'User Deleted',
                    detail: 'User has been deleted successfully',
                    life: 3000
                });
                fetchUsers();
            } catch (err) {
                console.error('Failed to delete user', err);
                toast.add({
                    severity: 'error',
                    summary: 'Delete Failed',
                    detail: err?.response?.data?.message || 'Failed to delete user.',
                    life: 5000
                });
            }
        },
    });
};
const userAdd = () => {
    if (!isSuperAdmin.value) return;
    currentView.value = "add";
};
const addUserFn = (userData) => {
    handleRegister(userData);
}
const editAdmin = async (rowData) => {
    if (!isSuperAdmin.value) return;
    try {
        const response = await getAdmin(rowData._id);
        adminDetails.value = response.data.data;
    } catch (err) {
        console.error("Unexpected error:", err);
    }
    currentView.value = "edit";
}
const handleRegister = async (values) => {
    isLoading.value = true;
    keycloakRole.value = await authStore.findKeycloackRole(values.role);
    try {
        const res = await authStore.register({
            username: values.username,
            email: values.email,
            password: values.password,
            role: keycloakRole.value,
            enabled: values.status
        });
        // await authStore.login(values.username, values.password, true);
        emit("registration-success", res.data.data);
        toast.add({
            severity: 'success',
            summary: 'Registration Successful',
            detail: 'User has been registered successfully',
            life: 3000
        });
        showList();
    } catch (err) {
        console.error("Registration failed:", err);
        let errorMessage = 'Registration failed. Please try again.';
        
        if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
        } else if (err.message) {
            errorMessage = err.message;
        }
        toast.add({
            severity: 'error',
            summary: 'Registration Failed',
            detail: errorMessage,
            life: 5000 
        });
    } finally {
        isLoading.value = false;
    }
};
const updateUserFn = async (updatedData) => {
    const id = updatedData._id;

    const updates = [
        { key: "username", label: "username", fn: updateAdminUsername, body: (v) => ({ username: v }) },
        { key: "email", label: "email", fn: updateAdminEmail, body: (v) => ({ email: v }) },
        { key: "role", label: "role", fn: updateAdminRole, body: (v) => ({ role: v }) },
        { key: "password", label: "password", fn: updateAdminPassword, body: (v) => ({ newPassword: v }) },
    ];

    for (const { key, label, fn, body } of updates) {
        if (updatedData[key]) {
            try {
                await fn(id, body(updatedData[key]));
            } catch (err) {
                toast.add({
                    severity: "error",
                    summary: `${label} Update Failed`,
                    detail: err?.response?.data?.message || `Failed to update ${label}.`,
                    life: 3000
                });
            }
        }
    }
    toast.add({
        severity: "success",
        summary: "Update Successfull",
        detail: "Update Successfull",
        life: 3000
    });

    fetchUsers();
    currentView.value = "list";
};
const cancelEdit = () => {
    console.log('cancelEdit function called');
    showList();
};
const onPageChange = ({ page, limit }) => {
    currentPage.value = page;
    rowsPerPage.value = limit;
    fetchUsers();
};
const showList = () => {
    fetchUsers();
    currentView.value = 'list';
};

const goBack = () => {
    showList();
};
</script>

<style>
.container {
    width: 100%;
    height: auto;
    border-radius: 8px;
}

.back-section {
    display: flex;
    align-items: center;
}

.back-button {
    color: var(--gray-600) !important;
    font-size: 12px !important;
}

.back-button:hover {
    color: var(--gray-600) !important;
    background-color: var(--gray-200) !important;
}

.back-button:active {
    color: var(--gray-600) !important;
}

.pi-arrow-left {
    color: var(--gray-600) !important;
    font-size: 12px !important;
}
</style>